import { createClient } from '@supabase/supabase-js';

// Zugangsdaten kommen bevorzugt aus den Build-Variablen (VITE_*). Fehlen sie —
// etwa weil das dist/ ohne .env gebaut wurde —, greift public/config.js, das
// beim Bauen unverändert nach dist/ kopiert wird und direkt auf dem Server
// bearbeitet werden kann. So lässt sich ein fertiges dist/ ausliefern, ohne die
// Zugangsdaten beim Bauen zu kennen.
// Feldnamen müssen exakt zu public/config.js passen.
interface RuntimeConfig {
  url?: string;
  anonKey?: string;
}

const PLACEHOLDER = /DEIN[-_]|DEINE[-_]|xxxxx/i;

const readRuntimeConfig = (): RuntimeConfig => {
  if (typeof window === 'undefined') return {};
  const cfg = (window as { __SUPABASE_CONFIG__?: RuntimeConfig }).__SUPABASE_CONFIG__;
  if (!cfg || typeof cfg !== 'object') return {};
  // Unausgefüllte Platzhalter zählen nicht als Konfiguration.
  const clean = (v?: string) => (v && !PLACEHOLDER.test(v) ? v : undefined);
  return { url: clean(cfg.url), anonKey: clean(cfg.anonKey) };
};

const runtimeConfig = readRuntimeConfig();

const supabaseUrl =
  (import.meta.env.VITE_SUPABASE_URL as string | undefined) || runtimeConfig.url;
const supabaseAnonKey =
  (import.meta.env.VITE_SUPABASE_ANON_KEY as string | undefined) || runtimeConfig.anonKey;

export const isSupabaseConfigured = Boolean(supabaseUrl && supabaseAnonKey);
export const supabaseConfigError = isSupabaseConfigured
  ? null
  : 'Supabase ist nicht konfiguriert: entweder VITE_SUPABASE_URL/VITE_SUPABASE_ANON_KEY beim Bauen setzen oder die Werte in config.js eintragen.';

if (!isSupabaseConfigured && import.meta.env.DEV) {
  console.warn(supabaseConfigError);
}

// Keep imports safe even when env vars are missing so public pages do not white-screen.
// Supabase calls will fail gracefully until the real project URL/key are configured.
export const supabase = createClient(
  supabaseUrl ?? 'https://missing-supabase-url.supabase.co',
  supabaseAnonKey ?? 'missing-supabase-anon-key'
);

// Type definitions for our registration data
export interface RegistrationData {
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  gender: string;
  age: number;
  instagram?: string;
  current_level: string;
  selected_course: string;
  start_date: string; // JSON string storing user preference like "asap", "contact-me", etc.
  payment_method: string;
  privacy_accepted: boolean;
  terms_accepted: boolean;
  withdrawal_consent: boolean;
  newsletter_opt_in: boolean;
  created_at?: string;
}

// Type definitions for contact form data
export interface ContactSubmissionData {
  name: string;
  email: string;
  subject: string;
  course?: string;
  message: string;
  privacy_accepted: boolean;
  created_at?: string;
}

// Type definitions for course data
export interface CourseData {
  id?: string;
  level: string;
  title: string;
  description: string;
  duration: string;
  sessions: string;
  color: string;
  books: string[];
  skills: string[];
  is_active: boolean;
  max_participants: number;
  price_monthly: number;
  price_full: number;
  created_at?: string;
  updated_at?: string;
}